import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, setTokens, clearTokens, getStoredUser, getAccessToken } from './client';

// Types
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: 'admin' | 'teacher' | 'student' | 'parent' | '';
  subject: string | null;
  is_active: boolean;
  is_verified: boolean;
  date_joined: string;
  date_of_birth: string | null;
}

export interface StudentProfile {
  id: string;
  student_id: string;
  date_of_birth: string | null;
  year_group: string;
  age_band: string;
  eal: boolean;
  first_language: string;
  curriculum_type: 'uk' | 'ib' | 'us' | 'uae' | 'other';
  speech_language_support: string;
  goals: string[];
  data_use_consent: boolean;
  terms_conditions_consent: boolean;
  parental_permission: boolean;
}

// Auth request/response types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface RegisterRequest {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  confirm_password: string;
}

export interface RegisterResponse {
  message: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

// Onboarding types
export interface OnboardingStep1Request {
  user_type: 'student' | 'parent' | 'teacher';
}

export interface OnboardingStep1Response {
  step: number;
  message: string;
  user_type: string;
}

export interface OnboardingStudentProfileRequest {
  date_of_birth: string;
  gender: 'male' | 'female' | 'non_binary' | 'prefer_not_to_say';
  year_group?: string;
  first_language?: string;
  eal?: boolean;
  curriculum_type?: 'uk' | 'ib' | 'us' | 'uae' | 'other';
  speech_language_support?: string;
}

export interface OnboardingParentProfileRequest {
  child_first_name: string;
  child_last_name: string;
  child_date_of_birth: string;
  year_group: string;
  first_language?: string;
  eal?: boolean;
  curriculum_type: 'uk' | 'ib' | 'us' | 'uae' | 'other';
  speech_language_support?: string;
}

export interface OnboardingTeacherProfileRequest {
  subject: string;
}

export interface OnboardingStep2Response {
  step: number;
  message: string;
  user_type: string;
}

export interface OnboardingStep3Request {
  goals: string[];
  data_use_consent: boolean;
  terms_conditions_consent: boolean;
  parental_permission?: boolean;
  profile_data?: OnboardingStudentProfileRequest | OnboardingParentProfileRequest | OnboardingTeacherProfileRequest;
}

export interface OnboardingStep3Response {
  user: User;
  student_profile: StudentProfile | null;
}

export interface OnboardingStatusResponse {
  is_onboarded: boolean;
  current_step: number | null;
  user_type: string | null;
  message: string;
  user?: User;
}

export interface RefreshResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface PasswordChangeRequest {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  new_password: string;
  confirm_password: string;
}

// Auth hooks
export function useLogin() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: LoginRequest): Promise<LoginResponse> => {
      const response = await apiClient.post<LoginResponse>('/auth/login/', data);
      return response.data;
    },
    onSuccess: (data) => {
      setTokens(data.access_token, data.refresh_token, data.user);
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      queryClient.invalidateQueries({ queryKey: ['onboardingStatus'] });
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: async (data: RegisterRequest): Promise<RegisterResponse> => {
      const response = await apiClient.post<RegisterResponse>('/auth/register/', data);
      return response.data;
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/auth/logout/');
    },
    onSuccess: () => {
      clearTokens();
      queryClient.clear();
    },
    onError: () => {
      // Clear tokens even if the server request fails
      clearTokens();
      queryClient.clear();
    },
  });
}

export function useRefreshToken() {
  return useMutation({
    mutationFn: async (): Promise<RefreshResponse> => {
      const response = await apiClient.post<RefreshResponse>('/auth/refresh/');
      return response.data;
    },
    onSuccess: (data) => {
      const refreshToken = localStorage.getItem('oracy_refresh_token');
      if (refreshToken) {
        setTokens(data.access_token, refreshToken);
      }
    },
  });
}

export function useCurrentUser() {
  return useQuery<User | null>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const token = getAccessToken();
      if (!token) return null;

      // Try to get from server first
      try {
        const response = await apiClient.get<User>('/auth/users/me/');
        const user = response.data;
        localStorage.setItem('oracy_user', JSON.stringify(user));
        return user;
      } catch {
        // Fallback to stored user
        const storedUser = getStoredUser();
        return storedUser;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: PasswordChangeRequest) => {
      const response = await apiClient.post('/auth/users/me/change_password/', data);
      return response.data;
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<User>) => {
      const response = await apiClient.patch('/auth/users/me/update/', data);
      return response.data;
    },
    onSuccess: (data) => {
      localStorage.setItem('oracy_user', JSON.stringify(data));
      queryClient.setQueryData(['currentUser'], data);
    },
  });
}

// Onboarding hooks
export function useOnboardingStatus() {
  return useQuery<OnboardingStatusResponse>({
    queryKey: ['onboardingStatus'],
    queryFn: async () => {
      const response = await apiClient.get<OnboardingStatusResponse>('/auth/onboarding/status/');
      return response.data;
    },
    enabled: !!getAccessToken(),
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useOnboardingStep1() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: OnboardingStep1Request): Promise<OnboardingStep1Response> => {
      const response = await apiClient.post<OnboardingStep1Response>('/auth/onboarding/step1/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });
}

export function useOnboardingStep2() {
  return useMutation({
    mutationFn: async (data: OnboardingStudentProfileRequest | OnboardingParentProfileRequest | OnboardingTeacherProfileRequest): Promise<OnboardingStep2Response> => {
      const response = await apiClient.post<OnboardingStep2Response>('/auth/onboarding/step2/', data);
      return response.data;
    },
  });
}

export function useOnboardingStep3() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: OnboardingStep3Request): Promise<OnboardingStep3Response> => {
      const response = await apiClient.post<OnboardingStep3Response>('/auth/onboarding/step3/', data);
      return response.data;
    },
    onSuccess: (data) => {
      // Update localStorage with the updated user data immediately
      // to prevent race conditions where components read stale data
      if (data.user) {
        localStorage.setItem('oracy_user', JSON.stringify(data.user));
      }
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      queryClient.invalidateQueries({ queryKey: ['onboardingStatus'] });
    },
  });
}

// Password reset hooks
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: async (data: PasswordResetRequest): Promise<{ message: string }> => {
      const response = await apiClient.post<{ message: string }>('/auth/password/reset/', data);
      return response.data;
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (data: PasswordResetConfirmRequest): Promise<{ message: string }> => {
      const response = await apiClient.post<{ message: string }>('/auth/password/reset/confirm/', data);
      return response.data;
    },
  });
}

// Auth helper to check if user is logged in
export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

// Auth helper to get user role
export function getUserRole(): string | null {
  const user = getStoredUser();
  return user?.role || null;
}

// Auth helper to check if user needs onboarding
export function needsOnboarding(): boolean {
  const user = getStoredUser();
  if (!user) return false;
  return !user.is_verified || !user.role;
}
